
import React, { useState, useEffect, useMemo } from 'react';
import {
    ShuffleIcon as Dice5,
    CheckIcon as Check,
    SmileIcon as Smile,
    UserIcon as User,
    ShirtIcon as Shirt,
    GlassesIcon as Glasses
} from '../components/Icons';

// --- Configuration & Options ---

// Translations (Tooltip/Fallback)
const translationMap = {
    // Top
    'longHair': 'Langes Haar', 'shortHair': 'Kurzes Haar', 'hat': 'Hut', 'hijab': 'Hidschab', 'turban': 'Turban',
    'winterHat01': 'Wintermütze 1', 'winterHat1': 'Wintermütze 1', 'winterHat02': 'Wintermütze 2', 'winterHat03': 'Wintermütze 3', 'winterHat04': 'Wintermütze 4',
    'bob': 'Bob', 'bun': 'Dutt', 'curly': 'Lockig', 'curvy': 'Wellig', 'dreads': 'Dreads', 'frida': 'Blumenkranz',
    'fro': 'Afro', 'froBand': 'Afro mit Band', 'longButNotTooLong': 'Mittellang', 'miaWallace': 'Mia', 'shavedSides': 'Undercut',
    'straight02': 'Glatt 1', 'straight01': 'Glatt 2', 'straightAndStrand': 'Glatt mit Strähne', 'dreads01': 'Dreads Kurz',
    'dreads02': 'Dreads Lang', 'frizzle': 'Kraus', 'shaggy': 'Zottelig', 'shaggyMullet': 'Vokuhila', 'shortCurly': 'Kurz Lockig',
    'shortFlat': 'Kurz Flach', 'shortRound': 'Kurz Rund', 'shortWaved': 'Kurz Wellig', 'sides': 'Seiten', 'theCaesar': 'Caesar',
    'theCaesarAndSidePart': 'Caesar Scheitel', 'bigHair': 'Voluminös',

    // Clothing
    'blazerAndShirt': 'Sakko & Hemd', 'blazerAndSweater': 'Sakko & Pulli', 'collarAndSweater': 'Kragen & Pulli',
    'graphicShirt': 'Motivhirt', 'hoodie': 'Kapuzenpulli', 'overall': 'Latzhose', 'shirtCrewNeck': 'Rundhals',
    'shirtScoopNeck': 'Ausschnitt', 'shirtVNeck': 'V-Ausschnitt',

    // Eyes
    'default': 'Normal', 'closed': 'Geschlossen', 'cry': 'Weinen', 'eyeRoll': 'Augenrollen', 'happy': 'Glücklich',
    'hearts': 'Verliebt', 'side': 'Seitlich', 'squint': 'Zusammengekniffen', 'surprised': 'Überrascht',
    'winkWacky': 'Zwinkern Frech', 'wink': 'Zwinkern', 'xDizzy': 'Schwindelig',

    // Eyebrows
    'defaultNatural': 'Natürlich', 'angry': 'Wütend', 'angryNatural': 'Wütend Natürlich', 'flatNatural': 'Flach',
    'raisedExcited': 'Aufgeregt', 'raisedExcitedNatural': 'Aufgeregt Natürlich', 'sadConcerned': 'Besorgt',
    'sadConcernedNatural': 'Besorgt Natürlich', 'unibrowNatural': 'Monobraue', 'upDown': 'Fragend', 'upDownNatural': 'Fragend Natürlich',

    // Mouth
    'concerned': 'Besorgt', 'disbelief': 'Ungläubig', 'eating': 'Kauen', 'grimace': 'Grimasse', 'sad': 'Traurig',
    'screamOpen': 'Schreien', 'serious': 'Ernst', 'smile': 'Lächeln', 'tongue': 'Zunge raus', 'twinkle': 'Grinsen', 'vomit': 'Übel',

    // Facial Hair
    'beardLight': 'Bart Stoppel', 'beardMajestic': 'Vollbart', 'beardMedium': 'Bart Mittel', 'moustacheFancy': 'Schick',
    'moustacheMagnum': 'Schnurrbart',

    // Graphics
    'skull': 'Totenkopf', 'diamond': 'Diamant', 'deer': 'Hirsch', 'pizza': 'Pizza', 'bear': 'Bär', 'bat': 'Fledermaus',
    'cumbia': 'Cumbia', 'hola': 'Hola', 'selena': 'Selena',

    // Accessories
    'kurt': 'Kurt', 'prescription01': 'Brille 1', 'prescription02': 'Brille 2', 'round': 'Rund',
    'sunglasses': 'Sonnenbrille', 'wayfarers': 'Wayfarers',
    'none': 'Keine'
};

const avatarOptions = {
    top: [
        'hat', 'hijab', 'turban',
        'bob', 'bun', 'curly', 'curvy', 'dreads', 'frida', 'fro', 'froBand', 'longButNotTooLong', 'miaWallace', 'shavedSides',
        'straight02', 'straight01', 'straightAndStrand', 'dreads01', 'dreads02', 'frizzle', 'shaggy', 'shaggyMullet', 'shortCurly',
        'shortFlat', 'shortRound', 'shortWaved', 'sides', 'theCaesar', 'theCaesarAndSidePart', 'bigHair'
    ],
    clothing: ['blazerAndShirt', 'blazerAndSweater', 'collarAndSweater', 'graphicShirt', 'hoodie', 'overall', 'shirtCrewNeck', 'shirtScoopNeck', 'shirtVNeck'],
    clothingGraphic: ['skull', 'diamond', 'deer', 'pizza', 'bear', 'bat', 'cumbia', 'hola'],
    eyes: ['default', 'closed', 'cry', 'eyeRoll', 'happy', 'hearts', 'side', 'squint', 'surprised', 'winkWacky', 'wink', 'xDizzy'],
    eyebrows: ['default', 'defaultNatural', 'angry', 'angryNatural', 'flatNatural', 'raisedExcited', 'raisedExcitedNatural', 'sadConcerned', 'sadConcernedNatural', 'unibrowNatural', 'upDown', 'upDownNatural'],
    mouth: ['default', 'concerned', 'disbelief', 'eating', 'grimace', 'sad', 'screamOpen', 'serious', 'smile', 'tongue', 'twinkle', 'vomit'],
    facialHair: ['none', 'beardLight', 'beardMajestic', 'beardMedium', 'moustacheFancy', 'moustacheMagnum'],
    accessories: ['none', 'kurt', 'prescription01', 'prescription02', 'round', 'sunglasses', 'wayfarers'],

    // Colors
    skinColor: ['edb98a', '614335', 'd08b5b', 'ae5d29', 'ffdbb4', 'fd9841', 'f8d25c'],
    hairColor: ['2c1b18', 'a55728', 'b58143', 'd6b370', '724133', '4a312c', 'f59797', 'ecdcbf', 'c93305', 'e8e1e1', '4a312c'],
    palette: ['25557c', '3c4f5c', '5199e4', '65c9ff', '929598', 'a7ffc4', 'b1e2ff', 'e6e6e6', 'ff488e', 'ff5c5c', 'ffffb1']
};

// --- Helper Functions ---

const rand = (arr) => arr[Math.floor(Math.random() * arr.length)];

const generateRandomConfig = () => ({
    seed: Math.random().toString(36).substring(7),
    top: rand(avatarOptions.top),
    hairColor: rand(avatarOptions.hairColor),
    hatColor: rand(avatarOptions.palette),
    eyes: rand(avatarOptions.eyes),
    eyebrows: rand(avatarOptions.eyebrows),
    mouth: rand(avatarOptions.mouth),
    facialHair: Math.random() > 0.7 ? rand(avatarOptions.facialHair.filter(x => x !== 'none')) : 'none',
    facialHairColor: rand(avatarOptions.hairColor),
    clothing: rand(avatarOptions.clothing),
    clothesColor: rand(avatarOptions.palette),
    clothingGraphic: rand(avatarOptions.clothingGraphic),
    accessories: Math.random() > 0.8 ? rand(avatarOptions.accessories.filter(x => x !== 'none')) : 'none',
    skinColor: rand(avatarOptions.skinColor)
});

const buildAvatarUrl = (config, overrides = {}) => {
    const c = { ...config, ...overrides };
    const baseUrl = 'https://api.dicebear.com/7.x/avataaars/svg';

    const params = new URLSearchParams();

    // Top/Hair
    if (c.top) params.append('top', c.top);
    if (c.hairColor) params.append('hairColor', c.hairColor);
    if (c.hatColor) params.append('hatColor', c.hatColor);

    // Face
    if (c.eyes) params.append('eyes', c.eyes);
    if (c.eyebrows) params.append('eyebrows', c.eyebrows);
    if (c.mouth) params.append('mouth', c.mouth);

    // Facial Hair
    if (c.facialHair && c.facialHair !== 'none') {
        params.append('facialHair', c.facialHair);
        if (c.facialHairColor) params.append('facialHairColor', c.facialHairColor);
    }

    // Clothing
    if (c.clothing) params.append('clothing', c.clothing);
    if (c.clothesColor) params.append('clothesColor', c.clothesColor);
    if (c.clothingGraphic && c.clothing === 'graphicShirt') {
        params.append('clothingGraphic', c.clothingGraphic);
    }

    // Accessories
    if (c.accessories && c.accessories !== 'none') {
        params.append('accessories', c.accessories);
    }

    // Skin Color
    if (c.skinColor) params.append('skinColor', c.skinColor);

    // Seed for consistency
    if (c.seed) params.append('seed', c.seed);

    return `${baseUrl}?${params.toString()}`;
};

// --- Components ---

const VisualOption = ({ value, label, isSelected, onClick, config, attributeKey }) => {
    const previewUrl = useMemo(() => buildAvatarUrl(config, { [attributeKey]: value }), [config, attributeKey, value]);

    return (
        <button
            onClick={onClick}
            className={`relative group aspect-square rounded-2xl overflow-hidden transition-all duration-200 border-2 
                ${isSelected
                    ? 'border-primary ring-2 ring-primary/20 ring-offset-2 ring-offset-surface'
                    : 'border-outlineVariant hover:border-primary/50 bg-surface-containerLow dark:bg-surface-containerHigh'}`}
            title={label}
        >
            <div className="absolute inset-0 p-2 flex items-center justify-center">
                <img src={previewUrl} alt={label} loading="lazy" className="w-full h-full object-contain" />
            </div>
            {isSelected && (
                <div className="absolute top-2 right-2 flex items-center justify-center w-5 h-5 bg-primary rounded-full text-primary-on shadow-sm">
                    <Check style={{ fontSize: 12 }} />
                </div>
            )}
        </button>
    );
};

const ColorOption = ({ color, isSelected, onClick }) => (
    <button
        onClick={onClick}
        className={`w-10 h-10 rounded-full border-2 transition-transform shadow-sm
            ${isSelected ? 'border-surface ring-2 ring-primary scale-110' : 'border-transparent hover:scale-110'}`}
        style={{ backgroundColor: `#${color}` }}
    />
);

const SubTabButton = ({ active, label, onClick }) => (
    <button
        onClick={onClick}
        className={`px-4 py-2 text-sm font-semibold rounded-full transition-all whitespace-nowrap
            ${active
                ? 'bg-primary text-primary-on shadow-md'
                : 'bg-surface-containerHigh text-surface-onVariant hover:bg-surface-containerHighest'}`}
    >
        {label}
    </button>
);

const MainTabButton = ({ active, label, icon: Icon, onClick }) => (
    <button
        onClick={onClick}
        className={`flex-1 flex flex-col items-center gap-1 py-3 px-2 text-xs font-medium transition-colors border-b-2
            ${active
                ? 'text-primary border-primary bg-primary/5'
                : 'text-surface-onVariant border-transparent hover:text-surface-on hover:bg-surface-on/5'}`}
    >
        <Icon style={{ fontSize: 20 }} />
        <span>{label}</span>
    </button>
);

// --- Main Editor Component ---

const AvatarEditor = ({ onSave, isSaving }) => {
    const [avatarConfig, setAvatarConfig] = useState(generateRandomConfig());

    const [mainTab, setMainTab] = useState('head');
    const [subTab, setSubTab] = useState('style');

    const previewUrl = useMemo(() => buildAvatarUrl(avatarConfig), [avatarConfig]);

    const randomize = () => {
        setAvatarConfig(generateRandomConfig());
    };

    const set = (key, val) => setAvatarConfig(p => ({ ...p, [key]: val }));

    const tabs = useMemo(() => {
        const top = avatarConfig.top.toLowerCase();
        const isHat = top.includes('hat') || top.includes('winter') || top.includes('turban') || top.includes('hijab');

        return {
            head: {
                label: 'Kopf',
                icon: Smile,
                subTabs: [
                    { id: 'style', label: 'Frisur', type: 'visual', key: 'top', options: [...avatarOptions.top] },
                    ...(!isHat ? [{ id: 'color', label: 'Haarfarbe', type: 'color', key: 'hairColor', options: [...avatarOptions.hairColor] }] : []),
                    { id: 'skin', label: 'Hautfarbe', type: 'color', key: 'skinColor', options: [...avatarOptions.skinColor] },
                    ...(isHat ? [{ id: 'hatColor', label: 'Hutfarbe', type: 'color', key: 'hatColor', options: [...avatarOptions.palette] }] : [])
                ]
            },
            face: {
                label: 'Gesicht',
                icon: User,
                subTabs: [
                    { id: 'eyes', label: 'Augen', type: 'visual', key: 'eyes', options: [...avatarOptions.eyes] },
                    { id: 'eyebrows', label: 'Brauen', type: 'visual', key: 'eyebrows', options: [...avatarOptions.eyebrows] },
                    { id: 'mouth', label: 'Mund', type: 'visual', key: 'mouth', options: [...avatarOptions.mouth] },
                    { id: 'facialHair', label: 'Bart', type: 'visual', key: 'facialHair', options: [...avatarOptions.facialHair] },
                    ...(avatarConfig.facialHair !== 'none'
                        ? [{ id: 'beardColor', label: 'Bartfarbe', type: 'color', key: 'facialHairColor', options: [...avatarOptions.hairColor] }]
                        : [])
                ]
            },
            accessories: {
                label: 'Extras',
                icon: Glasses,
                subTabs: [
                    { id: 'glasses', label: 'Brille', type: 'visual', key: 'accessories', options: [...avatarOptions.accessories] }
                ]
            },
            clothes: {
                label: 'Kleidung',
                icon: Shirt,
                subTabs: [
                    { id: 'style', label: 'Kleidung', type: 'visual', key: 'clothing', options: [...avatarOptions.clothing] },
                    { id: 'color', label: 'Farbe', type: 'color', key: 'clothesColor', options: [...avatarOptions.palette] },
                    ...(avatarConfig.clothing === 'graphicShirt'
                        ? [{ id: 'print', label: 'Aufdruck', type: 'visual', key: 'clothingGraphic', options: [...avatarOptions.clothingGraphic] }]
                        : [])
                ]
            }
        };
    }, [avatarConfig.top, avatarConfig.facialHair, avatarConfig.clothing]);

    const activeMainTabObj = tabs[mainTab];
    const currentSubTabs = activeMainTabObj.subTabs;
    const activeSubTabObj = currentSubTabs.find(t => t.id === subTab) || currentSubTabs[0];

    useEffect(() => {
        if (!currentSubTabs.find(t => t.id === subTab)) {
            setSubTab(currentSubTabs[0].id);
        }
    }, [mainTab, avatarConfig.top, avatarConfig.clothing, avatarConfig.facialHair, currentSubTabs, subTab]);


    return (
        <div className="flex flex-col lg:flex-row gap-8 h-full max-h-[800px] p-6">

            {/* Left Column: Preview */}
            <div className="flex flex-col items-center w-full lg:w-1/3 shrink-0">
                <div className="sticky top-0 w-full flex flex-col items-center">
                    <div className="relative w-64 h-64 md:w-72 md:h-72 mb-6">
                        {/* Background glow */}
                        <div className="absolute inset-0 bg-primary rounded-full opacity-10 blur-3xl transform scale-90"></div>
                        {/* Avatar container with theme-aware background */}
                        <div className="relative w-full h-full bg-gradient-to-br from-amber-100 to-orange-100 dark:from-primary-container dark:to-secondary-container rounded-full shadow-xl border-4 border-surface-containerHigh overflow-hidden">
                            <img src={previewUrl} alt="Vorschau" className="w-full h-full object-cover transition-opacity duration-300" />
                        </div>
                    </div>

                    <button
                        onClick={randomize}
                        className="md3-btn-tonal flex items-center gap-2 mb-8"
                    >
                        <Dice5 style={{ fontSize: 18 }} />
                        <span>Zufällig</span>
                    </button>

                    <button
                        onClick={() => onSave(previewUrl)}
                        disabled={isSaving}
                        className="md3-btn-filled w-full max-w-xs flex justify-center items-center gap-2 py-4 text-lg"
                    >
                        {isSaving ? (
                            <div className="w-5 h-5 border-2 border-current border-t-transparent rounded-full animate-spin" />
                        ) : (
                            <Check style={{ fontSize: 20 }} />
                        )}
                        Speichern
                    </button>
                </div>
            </div>

            {/* Right Column: Controls */}
            <div className="flex-1 bg-surface-containerLow dark:bg-surface-containerHigh rounded-2xl border border-outlineVariant shadow-sm flex flex-col min-w-0 h-[600px] lg:h-[600px] overflow-hidden">

                {/* Main Tabs */}
                <div className="flex border-b border-outlineVariant">
                    {Object.entries(tabs).map(([key, tab]) => (
                        <MainTabButton
                            key={key}
                            active={mainTab === key}
                            label={tab.label}
                            icon={tab.icon}
                            onClick={() => { setMainTab(key); }}
                        />
                    ))}
                </div>

                {/* Sub Tabs */}
                <div className="flex gap-2 p-4 overflow-x-auto border-b border-outlineVariant/50 scrollbar-hide bg-surface-container/30">
                    {currentSubTabs.map((tab) => (
                        <SubTabButton
                            key={tab.id}
                            active={activeSubTabObj.id === tab.id}
                            label={tab.label}
                            onClick={() => setSubTab(tab.id)}
                        />
                    ))}
                </div>

                {/* Options Grid */}
                <div className="flex-1 overflow-y-auto p-6 md:p-8 custom-scrollbar bg-surface-container/30">
                    {activeSubTabObj.type === 'visual' ? (
                        <div key="visual-grid" className="grid grid-cols-2 xs:grid-cols-3 sm:grid-cols-4 gap-4">
                            {activeSubTabObj.options.map(option => (
                                <VisualOption
                                    key={option}
                                    value={option}
                                    label={translationMap[option] || option}
                                    isSelected={avatarConfig[activeSubTabObj.key] === option}
                                    config={avatarConfig}
                                    attributeKey={activeSubTabObj.key}
                                    onClick={() => set(activeSubTabObj.key, option)}
                                />
                            ))}
                        </div>
                    ) : (
                        <div key="color-flex" className="flex flex-wrap gap-4 justify-center">
                            {activeSubTabObj.options.map(color => (
                                <ColorOption
                                    key={color}
                                    color={color}
                                    isSelected={avatarConfig[activeSubTabObj.key] === color}
                                    onClick={() => set(activeSubTabObj.key, color)}
                                />
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default AvatarEditor;
